import { useState, useMemo, useCallback } from 'react';
import { Loader2, Save, Shield, Info } from 'lucide-react';
import {
  useProjectDefaults,
  useSetProjectDefaults,
  PERMISSION_ROLES,
  type PermissionRole,
} from '../../hooks/usePermissions';
import { useProjects, type Environment } from '../../hooks/useProjects';
import { useToast } from '@/components/ui/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

interface ProjectDefaultsSettingsProps {
  projectId: string;
}

interface DefaultRoleState {
  [environmentName: string]: PermissionRole;
}

export function ProjectDefaultsSettings({ projectId }: ProjectDefaultsSettingsProps) {
  const toast = useToast();
  const { environments, isLoadingEnvironments } = useProjects();

  // Fetch current defaults
  const {
    data: defaultsData,
    isLoading: isLoadingDefaults,
    error: defaultsError,
  } = useProjectDefaults(projectId);

  // Set defaults mutation
  const setDefaultsMutation = useSetProjectDefaults(projectId);

  // Local state for editing - keyed by projectId to auto-reset when project changes
  const [editState, setEditState] = useState<{ projectId: string; edits: DefaultRoleState }>({
    projectId,
    edits: {},
  });

  // Get local edits, resetting if projectId changed
  const localEdits = useMemo(
    () => (editState.projectId === projectId ? editState.edits : {}),
    [editState, projectId]
  );

  // Compute current role states by merging server defaults with local edits
  const roleStates = useMemo(() => {
    const states: DefaultRoleState = {};
    environments.forEach((env) => {
      if (env.name in localEdits) {
        states[env.name] = localEdits[env.name];
      } else {
        const existing = defaultsData?.defaults.find(
          (d) => d.environment_name === env.name
        );
        states[env.name] = existing?.default_role ?? 'none';
      }
    });
    return states;
  }, [defaultsData, environments, localEdits]);

  // Check if there are unsaved changes (any local edits exist)
  const hasChanges = Object.keys(localEdits).length > 0;

  // Update local edits with current projectId
  const updateLocalEdits = useCallback((updater: (prev: DefaultRoleState) => DefaultRoleState) => {
    setEditState((prev) => ({
      projectId,
      edits: prev.projectId === projectId ? updater(prev.edits) : updater({}),
    }));
  }, [projectId]);

  const handleRoleChange = (environmentName: string, role: PermissionRole) => {
    updateLocalEdits((prev) => ({
      ...prev,
      [environmentName]: role,
    }));
  };

  const handleSave = async () => {
    const defaults = Object.entries(roleStates).map(([environment_name, default_role]) => ({
      environment_name,
      default_role,
    }));

    try {
      await setDefaultsMutation.mutateAsync(defaults);
      updateLocalEdits(() => ({})); // Clear local edits after successful save
      toast.success('Default permissions saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save defaults');
    }
  };

  const isLoading = isLoadingDefaults || isLoadingEnvironments;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Default Environment Permissions
        </CardTitle>
        <CardDescription>
          Set default access levels for new team members joining this project.
          Team admins always have full access to all environments.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : defaultsError ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            Failed to load default permissions
          </div>
        ) : environments.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
            No environments found. Create an environment first.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Info banner */}
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">
                These defaults apply when a new team member joins. Existing members' permissions are not affected.
                You can still set individual permissions per user that override these defaults.
              </p>
            </div>

            {/* Environment defaults list */}
            <div className="space-y-3">
              {environments.map((env) => (
                <EnvironmentDefaultRow
                  key={env.id}
                  environment={env}
                  value={roleStates[env.name] ?? 'none'}
                  onChange={(role) => handleRoleChange(env.name, role)}
                  disabled={setDefaultsMutation.isPending}
                />
              ))}
            </div>

            {/* Save button */}
            <div className="pt-4 border-t border-border">
              <Button
                onClick={handleSave}
                disabled={!hasChanges || setDefaultsMutation.isPending}
              >
                {setDefaultsMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Defaults
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface EnvironmentDefaultRowProps {
  environment: Environment;
  value: PermissionRole;
  onChange: (role: PermissionRole) => void;
  disabled?: boolean;
}

function EnvironmentDefaultRow({
  environment,
  value,
  onChange,
  disabled,
}: EnvironmentDefaultRowProps) {
  return (
    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
      <div>
        <Label className="text-sm font-medium">{environment.name}</Label>
        {environment.inherits_from && (
          <p className="text-xs text-muted-foreground">
            Inherits from {environment.inherits_from}
          </p>
        )}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as PermissionRole)}
        disabled={disabled}
        className="h-9 rounded-md border border-border bg-input px-3 text-sm focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring/20 disabled:opacity-50"
      >
        {PERMISSION_ROLES.map((role) => (
          <option key={role.value} value={role.value}>
            {role.label}
          </option>
        ))}
      </select>
    </div>
  );
}
